import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {
  DEFAULT_MEDIA_PREVIEW_TYPE,
  MEDIA_PREVIEW_CONFIG,
  MEDIA_PREVIEW_PLACEHOLDER
} from './mediaPreviewConfig';

/**
 * Converts a value to a percentage string relative to a total.
 * @param {number} value - The value to convert
 * @param {number} total - The total value to calculate percentage against
 * @returns {string} The percentage as a CSS string (e.g., "50%")
 */
const toPercentage = (value, total) => `${(value / total) * 100}%`;

const isPixelValueCharacter = (character) =>
  (character >= '0' && character <= '9') ||
  character === '-' ||
  character === '.';

const scalePixelValues = (value, scale) => {
  let scaledValue = '';
  let index = 0;

  while (index < value.length) {
    const start = index;

    while (index < value.length && isPixelValueCharacter(value[index])) {
      index += 1;
    }

    if (start === index) {
      scaledValue += value[index];
      index += 1;
      continue;
    }

    const pixelValue = value.slice(start, index);
    const numericValue = Number(pixelValue);

    if (
      value.slice(index, index + 2) === 'px' &&
      Number.isFinite(numericValue)
    ) {
      scaledValue += `${numericValue * scale}px`;
      index += 2;
    } else {
      scaledValue += pixelValue;
    }
  }

  return scaledValue;
};

/**
 * Scales a border radius value by a given scale factor.
 * Handles numeric, string (px), and non-px border radius values.
 * @param {number|string} borderRadius - The border radius to scale
 * @param {number} scale - The scale factor to apply
 * @returns {string} The scaled border radius value
 */
const scaleBorderRadius = (borderRadius, scale) => {
  if (typeof borderRadius === 'number') return `${borderRadius * scale}px`;
  if (typeof borderRadius !== 'string') return borderRadius;

  return scalePixelValues(borderRadius, scale);
};

/**
 * MediaPreview component that renders a media preview with configurable templates.
 * Supports foreground and background image layering with overlay positioning.
 * Falls back to a placeholder when no images are available or when images fail to load.
 *
 * @param {Object} props - Component props
 * @param {number|string} props.type - The preview type ID, defaults to DEFAULT_MEDIA_PREVIEW_TYPE
 * @param {number} props.scale - Scale factor for the preview (0-1), defaults to 1
 * @param {string|null} props.image - URL of the foreground image, defaults to null
 * @param {string|null} props.backgroundImage - URL of the background image, defaults to null
 * @param {number|string} props.borderRadius - Border radius for the overlay, defaults to 0
 */
function MediaPreview({
  type = DEFAULT_MEDIA_PREVIEW_TYPE,
  scale = 1,
  image = null,
  backgroundImage = null,
  borderRadius = 0
}) {
  // Track which images have failed to load
  const [failedImages, setFailedImages] = useState({});

  // Resolve the preview type, falling back to default if unsupported
  const resolvedType = MEDIA_PREVIEW_CONFIG[type]
    ? type
    : DEFAULT_MEDIA_PREVIEW_TYPE;
  const config = MEDIA_PREVIEW_CONFIG[resolvedType];

  // Normalize scale to ensure it's a valid number between 0 and 1
  const normalizedScale =
    Number.isFinite(scale) && scale > 0 ? Math.min(scale, 1) : 1;

  // Determine visibility of each layer based on image availability and load status
  const foregroundVisible = Boolean(image && !failedImages.foreground);
  const backgroundVisible = Boolean(
    backgroundImage && !failedImages.background
  );
  const showPlaceholder = !foregroundVisible && !backgroundVisible;

  // Reset failed image state when image URLs change
  useEffect(() => {
    setFailedImages({});
  }, [image, backgroundImage]);

  // Calculate overlay positioning and styling based on config and scale
  const overlayStyle = useMemo(() => {
    const {overlay, width, height} = config;
    const configuredRadius = borderRadius || overlay.borderRadius;

    return {
      left: toPercentage(overlay.x, width),
      top: toPercentage(overlay.y, height),
      width: toPercentage(overlay.width, width),
      height: toPercentage(overlay.height, height),
      borderRadius: scaleBorderRadius(configuredRadius, normalizedScale)
    };
  }, [borderRadius, config, normalizedScale]);

  /**
   * Handles image load errors by marking the layer as failed.
   * @param {string} layer - The layer that failed ('foreground' or 'background')
   */
  const handleImageError = (layer) => {
    setFailedImages((current) => ({...current, [layer]: true}));
  };

  return (
    <div
      className='relative w-full min-w-0 self-start'
      data-testid='media-preview'
      data-preview-type={resolvedType}
      style={{
        maxWidth: config.width * normalizedScale,
        aspectRatio: `${config.width} / ${config.height}`
      }}>
      {/* Base template image that provides the preview frame */}
      <img
        alt={`Media preview template ${resolvedType}`}
        className='block h-full w-full'
        draggable={false}
        src={config.template}
        style={{
          borderRadius: scaleBorderRadius(
            config.templateBorderRadius ?? 0,
            normalizedScale
          )
        }}
      />
      {/* Overlay container for media layers */}
      <div
        className='absolute overflow-hidden'
        data-testid='media-preview-overlay'
        style={overlayStyle}>
        {/* Placeholder shown when no images are available */}
        {showPlaceholder && (
          <img
            alt='Media placeholder'
            className='absolute inset-0 h-full w-full object-cover'
            data-testid='media-preview-placeholder'
            src={MEDIA_PREVIEW_PLACEHOLDER}
          />
        )}
        {/* Background image layer (rendered below foreground) */}
        {backgroundVisible && (
          <img
            alt='Background media preview'
            className='absolute inset-0 h-full w-full object-cover'
            data-testid='media-preview-background'
            onError={() => handleImageError('background')}
            src={backgroundImage}
          />
        )}
        {/* Foreground image layer (rendered on top) */}
        {foregroundVisible && (
          <img
            alt='Foreground media preview'
            className='absolute inset-0 h-full w-full object-cover'
            data-testid='media-preview-foreground'
            onError={() => handleImageError('foreground')}
            src={image}
          />
        )}
      </div>
    </div>
  );
}

MediaPreview.propTypes = {
  type: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  scale: PropTypes.number,
  image: PropTypes.string,
  backgroundImage: PropTypes.string,
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default React.memo(MediaPreview);
